import { FormProvider } from 'react-hook-form'
import Typography from '@mui/material/Typography'

// Component Imports
import Link from '@components/Link'

// Component Imports
import { InputAdornment, Box, IconButton } from '@mui/material'
import FormInput from '@/components/form/FormInput'

import { useTranslator } from '@/hooks/useTranslator'
import DirectionalIcon from '@components/theme/DirectionalIcon'
import SignUpStepHeader from '@/components/auth/signup/SignUpStepHeader'
import SignUpFormStepAction from '@/components/auth/signup/SignUpFormStepAction'
import { useRegisterStepForm } from '@/hooks/useRegisterStepForm'
import useMediaQuery from '@mui/material/useMediaQuery'
import InputDate from '@/components/form/InputDate'
import { useNotification, useRouteQuery } from '@/hooks'
import { useQuery } from '@tanstack/react-query'
import { Auth } from '@/@data'

const Register = () => {
  const { __ } = useTranslator()
  const n = useNotification()
  const { searchQuery } = useRouteQuery()

  const token = searchQuery.get('register_token')

  const { data: user, error } = useQuery({
    queryKey: ['user', token],
    queryFn: () => Auth.activeAccount(token ?? ''),
    enabled: !!token
  })

  useEffect(() => {
    if (user) {
      methods.setValue('email', user.email)
      methods.setValue('token', token || '')
      methods.setValue('phone', user.phone)
      methods.setValue('first_name', user.first_name)
      methods.setValue('employee_id', user.employee_id || 'N/A')
      methods.setValue('company_id', user.company?.id || 0)
      methods.setValue('company', user.company?.name || 'N/A')
      methods.setValue('birth_date', user.birth_date)
    }
  }, [user])

  useEffect(() => {
    if (error) {
      n.error(__('The invitation has expired or has been revoked. Please contact Support for further assistance.'))
    }
  }, [error])

  const handleClose = () => {
    methods.reset()
  }

  const { methods, activeStep, handleNext, handleBack, loadingForm, isStepValid } = useRegisterStepForm()

  const isMobile = useMediaQuery('(max-width: 768px)')

  const steps = [
    {
      labelPC: __('Verify Details'),
      labelMB: __('Step 01')
    },
    {
      labelPC: __('Set Password'),
      labelMB: __('Step 02')
    },
    {
      labelPC: __('You’re Done!'),
      labelMB: __('Step 03')
    }
  ]

  // step 3
  const [showPassword, setShowPassword] = useState(false)
  const [showPasswordConfirm, setShowConfirmPassword] = useState(false)

  const handleClickShowPassword = () => setShowPassword(show => !show)

  const password = methods.watch('password') || ''
  const passwordConfirm = methods.watch('confirm_password') || ''

  return (
    <>
      {token && (
        <div className='flex flex-col  gap-6 is-full sm:is-auto md:is-full sm:max-is-[400px] md:max-is-[unset] mbs-11 mbe-11 sm:mbs-14 md:mbs-0 md:mbe-0 bs-full md:min-bs-[80dvh] min-bs-[80dvh] md:px-0 px-5'>
          <Typography className='flex' color='primary.main'>
            <Link href='/cms/login' className='flex items-center gap-1.5'>
              <DirectionalIcon
                ltrIconClass='tabler-chevron-left'
                rtlIconClass='tabler-chevron-right'
                className='text-xl'
              />
              <span>{__('Back to login')}</span>
            </Link>
          </Typography>
          <FormProvider {...methods}>
            <form>
              <SignUpStepHeader
                steps={steps.map(step => ({ label: isMobile ? step.labelMB : step.labelPC }))}
                activeStep={activeStep}
              />

              <div className='flex flex-col gap-1 align-center justify-center items-center pt-[30px] pb-[30px]'>
                <img src='/images/pages/lift-logo.png' alt='' width={100} height={46} />
                <Typography variant='h4' className='md:text-[40px] text-[32px] leading-[38px] font-bold mt-8'>
                  {__('Sign Up')}
                </Typography>
                {(activeStep === 0 || activeStep === 1) && (
                  <>
                    <Typography className='md:text-[15px] text-[13px] leading-[22px] text-[##2F2B3DB2]'>
                      {__('Please double-check your personal information.')}
                    </Typography>
                    <Typography className='md:text-[15px] text-[13px] leading-[22px] text-[##2F2B3DB2]'>
                      {__('Let us know if anything needs updating!')}
                    </Typography>
                  </>
                )}
                {activeStep === 2 && (
                  <>
                    <Typography className='md:text-[15px] text-[13px] leading-[22px] text-[##2F2B3DB2] text-center'>
                      {__(
                        'You’ve registered successfully! Please wait while we automatically connect you to the system.'
                      )}
                    </Typography>
                  </>
                )}
              </div>

              <div className='flex flex-col gap-1 mt-5'>
                {activeStep === 0 && (
                  <>
                    <div className='grid gap-5'>
                      <div className='grid grid-cols-1 md:grid-cols-1 gap-4'>
                        <FormInput
                          id='email'
                          placeholder={__('Email')}
                          name='email'
                          control={methods.control}
                          label={__('Email')}
                          errorMessage={methods.formState.errors.email?.message}
                          className='rounded-full'
                          disabled={true}
                          slotProps={{
                            input: {
                              style: { padding: '5px 5px', borderRadius: '50px', marginTop: '4px' }
                            }
                          }}
                          sx={{
                            '& .MuiInputLabel-root': {
                              color: '#2F2B3DE5',
                              '&.Mui-disabled': {
                                color: '#2F2B3DE5'
                              }
                            }
                          }}
                        />
                      </div>

                      <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                        <FormInput
                          placeholder={__('Joni')}
                          name='first_name'
                          control={methods.control}
                          label={__('Full Name')}
                          errorMessage={methods.formState.errors.email?.message}
                          className='rounded-full'
                          disabled={true}
                          slotProps={{
                            input: {
                              style: { padding: '5px 5px', borderRadius: '50px', marginTop: '4px' }
                            }
                          }}
                          sx={{
                            '& .MuiInputLabel-root': {
                              color: '#2F2B3DE5',
                              '&.Mui-disabled': {
                                color: '#2F2B3DE5'
                              }
                            }
                          }}
                        />
                        <FormInput
                          placeholder={__('LF-543-9876')}
                          name='employee_id'
                          control={methods.control}
                          label={__('User Code')}
                          errorMessage={methods.formState.errors.employee_id?.message}
                          className='rounded-full'
                          disabled={true}
                          slotProps={{
                            input: {
                              style: { padding: '5px 5px', borderRadius: '50px', marginTop: '4px' }
                            }
                          }}
                          sx={{
                            '& .MuiInputLabel-root': {
                              color: '#2F2B3DE5',
                              '&.Mui-disabled': {
                                color: '#2F2B3DE5'
                              }
                            }
                          }}
                        />
                      </div>
                      <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                        <FormInput
                          placeholder={__('Company ABC')}
                          name='company'
                          control={methods.control}
                          label={__('Company')}
                          errorMessage={methods.formState.errors.email?.message}
                          className='rounded-full'
                          disabled={true}
                          slotProps={{
                            input: {
                              style: { padding: '6px 15px', borderRadius: '50px', marginTop: '4px' }
                            }
                          }}
                          sx={{
                            '& .MuiInputLabel-root': {
                              color: '#2F2B3DE5',
                              '&.Mui-disabled': {
                                color: '#2F2B3DE5'
                              }
                            }
                          }}
                        />
                        <div className='grid grid-cols-1 md:grid-cols-1'>
                          <div className='mb-1'>{__('D.O.B')}</div>
                          <InputDate
                            placeholder={__('Select your date')}
                            name='birth_date'
                            label={__('Date')}
                            value={methods.watch('birth_date')}
                            onChange={date => methods.setValue('birth_date', date)}
                            height={38}
                            className='rounded-full py-3'
                            disabled={true}
                            slotProps={{
                              input: {
                                style: { padding: '8px 15px', borderRadius: '50px', marginTop: '4px' }
                              }
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </>
                )}
                {activeStep === 1 && (
                  <Box className='flex flex-col gap-4 '>
                    <FormInput
                      className='rounded-full'
                      type={showPassword ? 'text' : 'password'}
                      id='outlined-adornment-password'
                      placeholder={__('Password')}
                      name='password'
                      control={methods.control}
                      errorMessage={methods.formState.errors.password?.message}
                      slotProps={{
                        input: {
                          style: { padding: '5px 20px', borderRadius: '50px', marginTop: '4px' },

                          startAdornment: (
                            <InputAdornment position='start'>
                              <i className='tabler-lock' style={{ color: '#2F2B3DE5', fontSize: 18 }} />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position='end'>
                              {methods.watch('password') && (
                                <>
                                  <IconButton
                                    size='small'
                                    onClick={() => methods.setValue('password', '')}
                                    tabIndex={-1}
                                  >
                                    <i className='tabler-circle-x' />
                                  </IconButton>
                                  <IconButton
                                    edge='end'
                                    onClick={handleClickShowPassword}
                                    onMouseDown={e => e.preventDefault()}
                                  >
                                    <i className={showPassword ? 'tabler-eye-off' : 'tabler-eye'} />
                                  </IconButton>
                                </>
                              )}
                            </InputAdornment>
                          )
                        }
                      }}
                    />
                    <FormInput
                      className='rounded-full'
                      type={showPasswordConfirm ? 'text' : 'password'}
                      id='outlined-adornment-password-confirm'
                      placeholder={__('Confirm Password')}
                      name='confirm_password'
                      control={methods.control}
                      errorMessage={methods.formState.errors.confirm_password?.message}
                      slotProps={{
                        input: {
                          style: { padding: '5px 20px', borderRadius: '50px', marginTop: '4px' },
                          startAdornment: (
                            <InputAdornment position='start'>
                              <i className='tabler-lock' style={{ color: '#2F2B3DE5', fontSize: 18 }} />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position='end'>
                              {passwordConfirm !== '' && (
                                <>
                                  <IconButton
                                    size='small'
                                    onClick={() => methods.setValue('confirm_password', '')}
                                    tabIndex={-1}
                                  >
                                    <i className='tabler-circle-x' />
                                  </IconButton>
                                  <IconButton
                                    edge='end'
                                    onClick={() => setShowConfirmPassword(show => !show)}
                                    onMouseDown={e => e.preventDefault()}
                                  >
                                    <i className={showPasswordConfirm ? 'tabler-eye-off' : 'tabler-eye'} />
                                  </IconButton>
                                </>
                              )}
                            </InputAdornment>
                          )
                        }
                      }}
                    />

                    <Box className='flex flex-col gap-2 mb-4'>
                      <Box display='flex' alignItems='center' gap={1}>
                        <Box
                          className='w-[10px] h-[10px] rounded-full '
                          bgcolor={password.length >= 6 ? '#197049' : '#e0e0e0'}
                        />
                        <Typography
                          className='text-[13px]  leading-[20px]'
                          color={password.length >= 6 ? '#197049' : '#4b465c'}
                        >
                          {__('Minimum 6 characters')}
                        </Typography>
                      </Box>
                      <Box display='flex' alignItems='center' gap={1}>
                        <Box
                          className='w-[10px] h-[10px] rounded-full '
                          bgcolor={/[0-9]/.test(password) ? '#197049' : '#e0e0e0'}
                        />
                        <Typography
                          className='text-[13px] leading-[20px]'
                          color={/[0-9]/.test(password) ? '#197049' : '#4b465c'}
                        >
                          {__('At least one numeral (0-9)')}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                )}
                {activeStep === 2 && <Box className='flex flex-col gap-4 '></Box>}
              </div>
              <SignUpFormStepAction
                handleBack={handleBack}
                handleNext={handleNext}
                steps={steps}
                activeStep={activeStep}
                handleClose={handleClose}
                loadingForm={loadingForm}
                isStepValid={isStepValid}
              />
            </form>
          </FormProvider>

          <div className='mt-auto text-center'>
            <span>{__('© 2025, Made with ❤️ by')}</span> <span className='text-primary'>{__('DOSI GROUP')}</span>
          </div>
        </div>
      )}
    </>
  )
}

export default Register
